import { CommonUIError } from '@afterpay/types'
import clsx from 'clsx'
import { MaskedDynamic } from 'imask'
import type { CountryCode } from 'libphonenumber-js'
import { getCountryCallingCode } from 'libphonenumber-js'
import React, { FocusEvent, FunctionComponent, isValidElement, KeyboardEvent, useEffect, useRef, useState } from 'react'

import { Dropdown } from '../Dropdown'
import { Icon } from '../Icon'
import { Input, InputBorderOverride, InputOnChange, InputOnFocus, InputSizes } from '../Input'
import { KINDS } from '../Input/constants'
import inputStyles from '../Input/style.module.scss'
import { useThemeClass } from '../Theme'
import { Span, Text } from '../Typography'
import { noop } from '../utils/function'
import { KeyCodes } from '../utils/keyboard'
import { getCountryEmoji as getCountryEmojiUtil, getFirstNonEmptyString } from '../utils/string'
import { AllowCountriesConfig, DEFAULT_CONFIG, LEADING_ZERO_FORMAT, PHONE_FORMAT } from './constants'
import { validator } from './helper'
import styles from './style.module.scss'

export const getCountryEmoji = getCountryEmojiUtil
export interface InputPhoneProps extends CommonUIError {
  allowedCountries?: AllowCountriesConfig[]
  selectedCountry?: CountryCode
  disabled?: boolean
  id?: string
  label?: string
  onChange?: (e: InputOnChange, countryRawValue: string, validity: boolean, rawValue?: string) => void
  onCountryChange?: (data: CountryCode) => void
  onBlur?: (e: InputOnFocus, rawValue: string, validity: boolean) => void
  readOnly?: boolean
  size?: InputSizes
  testNameSpace?: string
  value?: string
  autoFocus?: boolean
  autoSelectOnFocus?: boolean
  phoneFormat?: { [key: string]: any }
  skipDefaultValueValidation?: boolean
  singleCountryShowEmoji?: boolean // show country emoji even when there is only one allowed country
  showAlphanumericKeyboard?: boolean
  datadogActionName?: string
}

export const InputPhone: FunctionComponent<InputPhoneProps> = ({
  allowedCountries = DEFAULT_CONFIG,
  selectedCountry = 'AU',
  disabled = false,
  error = false,
  errorText = '',
  id = '',
  label = '',
  onChange = noop,
  onCountryChange = noop,
  onBlur = noop,
  readOnly = false,
  size = 'Large',
  testNameSpace = '',
  value = '',
  autoFocus = false,
  autoSelectOnFocus = true,
  phoneFormat = PHONE_FORMAT,
  skipDefaultValueValidation = false,
  singleCountryShowEmoji = false,
  showAlphanumericKeyboard = false,
  datadogActionName = undefined,
}) => {
  const componentRef = useRef<HTMLDivElement>(null)
  const shouldShowDropdown = allowedCountries.length > 1
  const selectedCountryIndex = allowedCountries.findIndex((c) => c.countryCode === selectedCountry)
  const isSelectedCountryValid = selectedCountryIndex >= 0
  const [country, setCountry] = useState<CountryCode>(isSelectedCountryValid ? selectedCountry : allowedCountries[0].countryCode)
  const [focusIndex, setFocusIndex] = useState(isSelectedCountryValid ? selectedCountryIndex : 0)
  const [inputError, setInputError] = useState(error)
  const [showMenu, setShowMenu] = useState(false)
  const [inputKey, setInputKey] = useState(id ?? Math.random().toString(36))
  const validate = validator(country)
  const countryRequiredLeadingZero = LEADING_ZERO_FORMAT[country] ?? false
  const menuId = `${id}-input-phone-countries-menu`
  const appendZerosString = countryRequiredLeadingZero && value[0] !== '0' ? '0' : ''
  const finalValue = `${appendZerosString}${value}`
  const valid = skipDefaultValueValidation || validate(finalValue)
  const errorId = `${getFirstNonEmptyString(id, testNameSpace)}-error`

  useEffect(() => {
    setInputError(error)
  }, [error])

  const handleChange = (e: InputOnChange, rawValue: string): void => {
    const appendZerosString = countryRequiredLeadingZero && rawValue[0] !== '0' ? '0' : ''
    const countryRawValue = `${appendZerosString}${rawValue}`
    const valid = validate(countryRawValue)
    onChange(e, countryRawValue, valid, rawValue)
  }

  const handleInputBlur = (e: InputOnFocus): void => {
    const rawValue = e.target.dataset.rawdata ?? ''
    const appendZerosString = countryRequiredLeadingZero && rawValue[0] !== '0' ? '0' : ''
    const countryRawValue = `${appendZerosString}${rawValue}`
    const valid = validate(countryRawValue)
    setInputError(!valid)
    onBlur(e, countryRawValue, valid)
  }

  const maskOptions = {
    mask: phoneFormat[country] ?? phoneFormat.DEFAULT,
    dispatch: (appended: string, dynamicMasked: MaskedDynamic) => {
      const currentValue = `${dynamicMasked.unmaskedValue}${appended}`
      // @ts-expect-error
      return dynamicMasked.compiledMasks.find((mask) => currentValue.indexOf(mask.startsWith) === 0)
    },
  }

  const handleCountriesClick = (): void => {
    if (!disabled && !readOnly) {
      setShowMenu(!showMenu)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (disabled ?? readOnly) {
      return
    }

    const { key } = e
    if (key === KeyCodes.Enter) {
      e.preventDefault()
      if (showMenu) {
        setCountry(allowedCountries[focusIndex].countryCode)
        setInputError(false)
        setInputKey(Math.random().toString(36))
        onCountryChange(allowedCountries[focusIndex].countryCode)
      }
      setShowMenu(!showMenu)
    }

    if (key === KeyCodes.ArrowDown) {
      e.preventDefault()
      setFocusIndex(focusIndex >= allowedCountries.length - 1 ? 0 : focusIndex + 1)
    }
    if (key === KeyCodes.ArrowUp) {
      e.preventDefault()
      setFocusIndex(focusIndex === 0 ? allowedCountries.length - 1 : focusIndex - 1)
    }
    if (key === KeyCodes.Escape) {
      e.preventDefault()
      setShowMenu(false)
    }
  }

  const handleEscape = (e: FocusEvent<HTMLButtonElement> & { relatedTarget: HTMLElement | null }): void => {
    if (e.relatedTarget?.parentElement?.id !== menuId) {
      setShowMenu(false)
    }
  }

  const handleCountryClick = (countryCode: CountryCode, index: number): void => {
    setFocusIndex(index)
    setCountry(countryCode)
    setShowMenu(false)
    setInputError(false)
    setInputKey(Math.random().toString(36))

    onCountryChange(countryCode)
  }

  const themeClass = useThemeClass()
  const componentStyles = clsx(styles.component, showMenu && styles.expanded, styles[`${themeClass}ComponentWrapper`], styles[themeClass])
  const inputWrapperStyles = clsx(styles.inputWrapper, (shouldShowDropdown || singleCountryShowEmoji) && styles.paddedInputWrapper)
  const componentClassNames = clsx((shouldShowDropdown || singleCountryShowEmoji) && styles.disabledLeftBorderRadius, inputError && styles.modError)
  const overrideBorderRadiusProps = {
    override: InputBorderOverride.DisabledLeftBorderRadius,
  }
  const countriesButtonStyles = clsx(styles.countryButton, disabled && styles.disabled, readOnly && styles.readOnly, inputError && styles.error)

  const inputComponentStyle = clsx(inputStyles.input, styles.input)
  const countriesIconStyles = clsx(styles.countryIconWrapper, inputError && styles.error, disabled && styles.disabled)

  useEffect(() => {
    if (showMenu) {
      componentRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [showMenu])

  return (
    <div className={componentStyles} ref={componentRef}>
      <div className={styles.componentWrapper} data-dd-action-name={datadogActionName ?? `${testNameSpace} Input`}>
        {shouldShowDropdown ? (
          <button
            tabIndex={disabled ? -1 : 0}
            className={countriesButtonStyles}
            onClick={handleCountriesClick}
            onKeyDown={handleKeyDown}
            onBlur={handleEscape}
            aria-label={`${allowedCountries[focusIndex].name} ${label}`}
            type='button'
            {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-countries-toggle` })}
          >
            {getCountryEmoji(country)}{' '}
            <span className={styles.iconWrapper}>
              <Icon.CaretDown size='ExtraSmall' title='Select a country' />
            </span>
          </button>
        ) : (
          singleCountryShowEmoji && (
            <div className={countriesIconStyles}>
              <span className={styles.countryIcon}>{getCountryEmoji(country)}</span>
            </div>
          )
        )}
        <div className={inputWrapperStyles}>
          <div className={componentClassNames}>
            <Input
              kind={showAlphanumericKeyboard ? KINDS.EMAIL : KINDS.TEL}
              maskOptions={maskOptions}
              key={inputKey}
              size={size}
              disabled={disabled}
              readOnly={readOnly}
              label={label}
              name='mobile'
              autoComplete='tel'
              className={inputComponentStyle}
              onChange={handleChange}
              onBlur={handleInputBlur}
              value={valid ? value : ''}
              error={inputError}
              autoFocus={autoFocus}
              autoSelectOnFocus={autoSelectOnFocus}
              {...((shouldShowDropdown || singleCountryShowEmoji) && overrideBorderRadiusProps)}
              {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-number` })}
              {...(errorId.length > 0 && error && { 'aria-describedby': errorId })}
            />
          </div>
        </div>
      </div>
      {inputError && ((typeof errorText === 'string' && errorText.length > 0) || isValidElement(errorText)) && (
        <Span className={styles.errorText} color={'Fire'} size='XS' id={errorId} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-error` })}>
          {errorText}
        </Span>
      )}
      {showMenu && (
        <div className={styles.menuWrapper}>
          <Dropdown.Menu
            key={menuId}
            id={menuId}
            aria-activedescendant={`input-phone-countries-menu_${focusIndex}`}
            {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-countries` })}
          >
            {allowedCountries.map((allowedCountry, index) => {
              const { countryCode, name } = allowedCountry
              return (
                <Dropdown.MenuItem
                  key={`${id}-input-phone-country-${countryCode as string}`}
                  focus={focusIndex === index}
                  onClick={() => handleCountryClick(countryCode, index)}
                  {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-country-name-${countryCode.toLowerCase()}` })}
                >
                  <span className={styles.flagEmoji}>{getCountryEmoji(countryCode)}</span>
                  <span className={styles.countryName}>{name}</span>
                  <Text renderAs='span' color='Gray40'>
                    +{getCountryCallingCode(countryCode)}
                  </Text>
                </Dropdown.MenuItem>
              )
            })}
          </Dropdown.Menu>
        </div>
      )}
    </div>
  )
}
